/**
 * TeamsModule — CRUD for GOW crews (teams).
 *
 * Simple by design: free-text foreman/truck/nickname + multi-select of real
 * users. Deactivation is soft (is_active=false) and one-click with a
 * 6-second undo toast so managers can restore instantly if they misclicked.
 * Historical service orders keep their assigned_user_ids snapshot untouched.
 */
import { useState, useEffect, useCallback } from "react";
import { useApi } from "./AuthContext";
import {
  Users, Plus, Pencil, Trash2, X, Check, Truck,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", foreman_name: "", truck: "", nickname: "", member_user_ids: [] };

export default function TeamsModule() {
  const api = useApi();
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showInactive, setShowInactive] = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/workforce/teams${showInactive ? "?include_inactive=true" : ""}`);
      setTeams(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Failed to load teams"); }
    finally { setLoading(false); }
  }, [api, showInactive]);

  useEffect(() => {
    fetchTeams();
    api.get("/admin/workforce/members")
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
  }, [api, fetchTeams]);

  const resetForm = () => { setForm(EMPTY_FORM); setEditing(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/workforce/teams/${editing.id}`, form);
        toast.success("Team updated");
      } else {
        await api.post("/admin/workforce/teams", form);
        toast.success("Team created");
      }
      resetForm();
      fetchTeams();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const handleEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      foreman_name: t.foreman_name || "",
      truck: t.truck || "",
      nickname: t.nickname || "",
      member_user_ids: Array.isArray(t.member_user_ids) ? [...t.member_user_ids] : [],
    });
    setShowForm(true);
  };

  // One-click soft delete with a 6s undo toast — no confirmation modal.
  const handleDeactivate = async (t) => {
    try {
      await api.delete(`/admin/workforce/teams/${t.id}`);
      fetchTeams();
      toast(`"${t.name}" deactivated`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/admin/workforce/teams/${t.id}`, { is_active: true });
              toast.success("Team restored");
              fetchTeams();
            } catch (_) { toast.error("Undo failed"); }
          },
        },
      });
    } catch (err) { toast.error("Failed to deactivate"); }
  };

  const handleReactivate = async (t) => {
    try {
      await api.put(`/admin/workforce/teams/${t.id}`, { is_active: true });
      toast.success("Team reactivated");
      fetchTeams();
    } catch (_) { toast.error("Failed to reactivate"); }
  };

  const toggleMember = (uid) => {
    setForm((f) => {
      const set = new Set(f.member_user_ids || []);
      if (set.has(uid)) set.delete(uid); else set.add(uid);
      return { ...f, member_user_ids: Array.from(set) };
    });
  };

  return (
    <div data-testid="teams-module" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Teams (Crews)</h2>
            <p className="text-sm text-stone-500">{teams.length} team{teams.length !== 1 ? "s" : ""} — used to schedule Service Orders per crew per week.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-stone-500 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-forest-600" />
            Show inactive
          </label>
          <button
            data-testid="add-team-btn"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> New Team
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="team-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-800">{editing ? "Edit Team" : "New Team"}</h3>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Team Name *</label>
                <input data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required minLength={1} maxLength={100}
                  placeholder="e.g. Crew A"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nickname</label>
                <input data-testid="team-nickname-input" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                  placeholder='e.g. "The Rockets"'
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Foreman</label>
                <input data-testid="team-foreman-input" value={form.foreman_name} onChange={(e) => setForm({ ...form, foreman_name: e.target.value })}
                  placeholder="Free text — e.g. John Silva"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Truck</label>
                <input data-testid="team-truck-input" value={form.truck} onChange={(e) => setForm({ ...form, truck: e.target.value })}
                  placeholder="e.g. Ford F-150 · ABC-1234"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Members</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border border-stone-200 rounded-lg bg-stone-50/50 max-h-56 overflow-y-auto">
                {members.length === 0 ? (
                  <p className="text-xs text-stone-400 col-span-full">No workforce members yet. Add them in Workforce first.</p>
                ) : members.map((m) => {
                  const checked = (form.member_user_ids || []).includes(m.id);
                  return (
                    <label key={m.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer transition-colors ${checked ? "bg-forest-50 border-forest-400" : "bg-white border-stone-200 hover:bg-stone-100"}`}
                      data-testid={`team-member-${m.id}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleMember(m.id)} className="accent-forest-600" />
                      <span className="text-xs text-stone-700 truncate">{m.name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-stone-400 mt-1">
                When you schedule a Service Order to this team, its current members are snapshotted onto the ticket.
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              <button data-testid="team-submit-btn" type="submit"
                className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">
                <Check className="w-4 h-4" />{editing ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm}
                className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-stone-500">Loading...</div>
        ) : teams.length === 0 ? (
          <div className="text-center py-12 text-stone-400">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No teams yet — click "New Team" to add your first crew.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="teams-table">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-3 font-medium text-stone-600">Team</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600 hidden md:table-cell">Foreman</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600 hidden md:table-cell">Truck</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600">Members</th>
                  <th className="text-center px-4 py-3 font-medium text-stone-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id} className={`border-b border-stone-100 hover:bg-stone-50 ${t.is_active === false ? "opacity-60" : ""}`} data-testid={`team-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-stone-800">{t.name}</div>
                      {t.nickname && <div className="text-[11px] text-stone-500 italic">"{t.nickname}"</div>}
                    </td>
                    <td className="px-4 py-3 text-stone-600 hidden md:table-cell">{t.foreman_name || "—"}</td>
                    <td className="px-4 py-3 text-stone-600 hidden md:table-cell">
                      {t.truck ? (<span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-stone-400" />{t.truck}</span>) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {(t.members || []).length === 0 ? (
                          <span className="text-[11px] text-stone-400">No members</span>
                        ) : (t.members || []).slice(0, 4).map((m) => (
                          <span key={m.id} title={m.name} className="w-6 h-6 rounded-full bg-forest-100 text-forest-700 text-[10px] font-semibold flex items-center justify-center border border-forest-200">
                            {(m.name || "?").slice(0, 2).toUpperCase()}
                          </span>
                        ))}
                        {(t.members || []).length > 4 && (
                          <span className="text-[10px] text-stone-500">+{(t.members || []).length - 4}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.is_active !== false ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"}`}>
                        {t.is_active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(t)} data-testid={`team-edit-${t.id}`} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-forest-600"><Pencil className="w-4 h-4" /></button>
                        {t.is_active !== false ? (
                          <button onClick={() => handleDeactivate(t)} data-testid={`team-deactivate-${t.id}`} className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600" title="Deactivate (one click, undo available)"><Trash2 className="w-4 h-4" /></button>
                        ) : (
                          <button onClick={() => handleReactivate(t)} data-testid={`team-reactivate-${t.id}`} className="text-xs font-medium text-forest-600 hover:text-forest-700 px-2">Reactivate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
